import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Drivers from './pages/Drivers';
import Jobs from './pages/Jobs';
import CreateJob from './pages/CreateJob';
import JobTracking from './pages/JobTracking';
import Admins from './pages/Admins';
import Vendors from './pages/Vendors';
import Pricing from './pages/Pricing';
import Withdrawals from './pages/Withdrawals';
import Offers from './pages/Offers';
import VendorDashboard from './pages/VendorDashboard';
import VendorFleet from './pages/VendorFleet';
import VendorJobs from './pages/VendorJobs';
import VendorEarnings from './pages/VendorEarnings';
import './App.css';

const PrivateRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div className="loading-screen">Loading...</div>;
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated, isVendor } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" /> : <Login />}
      />

      <Route
        path="/"
        element={
          <PrivateRoute>
            <Layout>
              {isVendor ? <VendorDashboard /> : <Dashboard />}
            </Layout>
          </PrivateRoute>
        }
      />

      <Route
        path="/drivers"
        element={
          <PrivateRoute>
            <Layout>
              <Drivers />
            </Layout>
          </PrivateRoute>
        }
      />

      <Route
        path="/jobs"
        element={
          <PrivateRoute>
            <Layout>
              <Jobs />
            </Layout>
          </PrivateRoute>
        }
      />

      <Route
        path="/jobs/create"
        element={
          <PrivateRoute>
            <Layout>
              <CreateJob />
            </Layout>
          </PrivateRoute>
        }
      />

      <Route
        path="/jobs/:jobId/track"
        element={
          <PrivateRoute>
            <Layout>
              <JobTracking />
            </Layout>
          </PrivateRoute>
        }
      />

      <Route
        path="/admins"
        element={
          <PrivateRoute>
            <Layout>
              <Admins />
            </Layout>
          </PrivateRoute>
        }
      />

      <Route
        path="/vendors"
        element={
          <PrivateRoute>
            <Layout>
              <Vendors />
            </Layout>
          </PrivateRoute>
        }
      />

      <Route
        path="/pricing"
        element={
          <PrivateRoute>
            <Layout>
              <Pricing />
            </Layout>
          </PrivateRoute>
        }
      />

      <Route
        path="/withdrawals"
        element={
          <PrivateRoute>
            <Layout>
              <Withdrawals />
            </Layout>
          </PrivateRoute>
        }
      />

      <Route
        path="/offers"
        element={
          <PrivateRoute>
            <Layout>
              <Offers />
            </Layout>
          </PrivateRoute>
        }
      />

      <Route
        path="/vendor-fleet"
        element={
          <PrivateRoute>
            <Layout>
              <VendorFleet />
            </Layout>
          </PrivateRoute>
        }
      />

      <Route
        path="/vendor-jobs"
        element={
          <PrivateRoute>
            <Layout>
              <VendorJobs />
            </Layout>
          </PrivateRoute>
        }
      />

      <Route
        path="/vendor-earnings"
        element={
          <PrivateRoute>
            <Layout>
              <VendorEarnings />
            </Layout>
          </PrivateRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
// Cache bust: $(date +%s)
